import { create } from 'zustand';
import axiosInstance from '../lib/axios';

// T-198: los 16 comandos preinstalados ahora los siembra el backend como
// comandos de SISTEMA (T-197). Esta constante ya NO se siembra en el plano
// usuario; se conserva únicamente como referencia para la migración anti-
// duplicados de localStorage viejo (filtrar por id + título idéntico).
const _LEGACY_SEED = [
  {
    id: '1',
    title: 'Escritor Creativo',
    instruction: 'Reescribe el siguiente texto de manera más creativa y literaria, manteniendo el significado original pero usando un lenguaje más elaborado y poético.',
    category: 'Texto'
  },
  {
    id: '2',
    title: 'Simplificar',
    instruction: 'Simplifica el siguiente texto para que sea más fácil de entender, usando palabras más simples y oraciones más cortas.',
    category: 'Texto'
  },
  {
    id: '3',
    title: 'Profesional',
    instruction: 'Reescribe el siguiente texto en un tono profesional y formal, apropiado para contextos de negocios o académicos.',
    category: 'Texto'
  },
  {
    id: '4',
    title: 'Resumir',
    instruction: 'Resume el siguiente texto en sus puntos principales, manteniendo solo la información más importante.',
    category: 'Texto'
  },
  {
    id: '5',
    title: 'Expandir',
    instruction: 'Expande el siguiente texto agregando más detalles, ejemplos y explicaciones para hacerlo más completo.',
    category: 'Texto'
  },
  {
    id: '6',
    title: 'Corregir Gramática',
    instruction: 'Corrige cualquier error gramatical, ortográfico o de puntuación en el siguiente texto.',
    category: 'Texto'
  },
  // T-053: la lista COMPLETA de estilos artísticos (espejo de IMAGE_STYLES en
  // ImageGenerateDialog.js) como comandos de generación. Cada uno inyecta su
  // descriptor de estilo al prompt del usuario.
  { id: '7',  title: 'Imagen Realista',    instruction: 'Genera una imagen en estilo realista. Aplica: photorealistic, high quality, detailed. Basada en la siguiente descripción:', category: 'Imagen' },
  { id: '8',  title: 'Imagen Artística',   instruction: 'Genera una imagen en estilo artístico. Aplica: artistic, creative, expressive. Basada en la siguiente descripción:', category: 'Imagen' },
  { id: '11', title: 'Imagen Caricatura',  instruction: 'Genera una imagen en estilo caricatura. Aplica: cartoon style, colorful, fun. Basada en la siguiente descripción:', category: 'Imagen' },
  { id: '12', title: 'Imagen Boceto',      instruction: 'Genera una imagen en estilo boceto. Aplica: pencil sketch, hand-drawn, artistic. Basada en la siguiente descripción:', category: 'Imagen' },
  { id: '13', title: 'Imagen Acuarela',    instruction: 'Genera una imagen en estilo acuarela. Aplica: watercolor painting, soft colors, artistic. Basada en la siguiente descripción:', category: 'Imagen' },
  { id: '14', title: 'Imagen Arte Digital', instruction: 'Genera una imagen en estilo arte digital. Aplica: digital art, modern, vibrant. Basada en la siguiente descripción:', category: 'Imagen' },
  { id: '15', title: 'Imagen Óleo',        instruction: 'Genera una imagen en estilo óleo. Aplica: oil painting, classical, textured. Basada en la siguiente descripción:', category: 'Imagen' },
  { id: '16', title: 'Imagen Minimalista', instruction: 'Genera una imagen en estilo minimalista. Aplica: minimalist, simple, clean design. Basada en la siguiente descripción:', category: 'Imagen' },
  { id: '17', title: 'Imagen Abstracta',   instruction: 'Genera una imagen en estilo abstracto. Aplica: abstract art, creative, unique. Basada en la siguiente descripción:', category: 'Imagen' },
  { id: '18', title: 'Imagen Render 3D',   instruction: 'Genera una imagen en estilo render 3D. Aplica: 3D render, professional, detailed. Basada en la siguiente descripción:', category: 'Imagen' },
  {
    id: '9',
    title: 'Describir Imagen Detallado',
    instruction: 'Proporciona una descripción detallada y completa de la imagen, incluyendo todos los elementos visibles, colores, composición y atmósfera.',
    category: 'Descripción'
  },
  {
    id: '10',
    title: 'Describir Imagen Simple',
    instruction: 'Describe brevemente los elementos principales de la imagen de forma clara y concisa.',
    category: 'Descripción'
  }
];

// T-198: índices para la migración anti-duplicados (id → comando sembrado).
const _LEGACY_SEED_BY_ID = new Map(_LEGACY_SEED.map((c) => [c.id, c]));

/**
 * T-034 — normalize a command to the multi-step (`steps`) shape.
 *
 * Backward compatible migration: a legacy command with a top-level
 * `instruction` and no `steps` becomes `steps: [{ instruction }]`. The
 * top-level `instruction` is KEPT as a mirror of `steps[0].instruction` so
 * legacy consumers (QuickCommandModal, card previews) keep working unchanged.
 * A command with 2+ steps is a "macro".
 */
export const normalizeCommand = (cmd) => {
  if (!cmd || typeof cmd !== 'object') return cmd;

  let steps;
  if (Array.isArray(cmd.steps) && cmd.steps.length > 0) {
    steps = cmd.steps
      .filter((s) => s && typeof s.instruction === 'string')
      // T-231: preservar el model y advisors declarados por paso para que el
      // orquestador de macros (EditorPage) los honre al ejecutar el comando.
      .map((s) => ({
        instruction: s.instruction,
        model: typeof s.model === 'string' ? s.model : '',
        advisors: Array.isArray(s.advisors)
          ? s.advisors.filter((a) => typeof a === 'string' && a.trim()).slice(0, 3)
          : []
      }));
  }
  if (!steps || steps.length === 0) {
    // Legacy command (or malformed steps): fall back to the single instruction.
    // T-231: carry empty model/advisors so the orchestrator shape stays uniform.
    steps = [{ instruction: cmd.instruction || '', model: '', advisors: [] }];
  }

  return {
    ...cmd,
    steps,
    // `system` flag (T-037) passes through untouched via the spread above.
    // Mirror of steps[0] so consumers reading `command.instruction` still work.
    instruction: steps[0].instruction
  };
};

export const normalizeCommands = (list) =>
  Array.isArray(list) ? list.map(normalizeCommand) : list;

// T-037: only USER commands are persisted to localStorage; system commands
// (`system:true`) are re-fetched from the backend on each load, never stored.
const persistUserCommands = (commands) => {
  const userOnly = commands.filter((c) => !c.system);
  localStorage.setItem('ai_commands', JSON.stringify(userOnly));
};

export const useCommandsStore = create((set, get) => {
  // T-198: user commands come from localStorage only; system commands are
  // fetched from the backend via loadServerDefaults. No more default seeding.
  const stored = localStorage.getItem('ai_commands');
  let initialCommands = stored ? JSON.parse(stored) : [];

  // T-198: migración anti-duplicados. Un usuario con localStorage viejo puede
  // tener copias de los comandos sembrados (ids '1'..'18'). Se filtran los que
  // coincidan en id Y título idéntico al default sembrado (doble condición para
  // no borrar un comando propio que casualmente use un id numérico). Los
  // duplicados se eliminan del localStorage (no se re-persisten).
  if (stored && Array.isArray(initialCommands)) {
    const before = initialCommands.length;
    initialCommands = initialCommands.filter((c) => {
      const seedCmd = _LEGACY_SEED_BY_ID.get(String(c.id));
      if (seedCmd && seedCmd.title === c.title) return false; // duplicate of a seeded command
      return true;
    });
    if (initialCommands.length !== before) {
      persistUserCommands(initialCommands);
    }
  }

  initialCommands = normalizeCommands(initialCommands);

  return {
    commands: initialCommands,

    // T-037: fetch admin system commands and merge them (flagged system:true)
    // without touching the user's own commands or localStorage. Re-merged on
    // every call (existing system entries are replaced by the fresh set).
    loadServerDefaults: async () => {
      try {
        const res = await axiosInstance.get('/commands/system');
        const serverCmds = normalizeCommands(
          (res.data || []).map((c) => ({ ...c, system: true }))
        );
        set((state) => ({
          commands: [...state.commands.filter((c) => !c.system), ...serverCmds]
        }));
      } catch (e) {
        // Best-effort: keep local commands if the backend is unreachable.
      }
    },

    addCommand: (command) => {
      set((state) => {
        // System commands are read-only from the backend; never add one here.
        const newCommand = normalizeCommand({
          ...command,
          system: false,
          id: Date.now().toString()
        });
        const updatedCommands = [...state.commands, newCommand];
        persistUserCommands(updatedCommands);
        return { commands: updatedCommands };
      });
    },

    updateCommand: (id, updates) => {
      set((state) => {
        const updatedCommands = state.commands.map(cmd =>
          // System commands cannot be edited from the client.
          (cmd.id === id && !cmd.system) ? normalizeCommand({ ...cmd, ...updates }) : cmd
        );
        persistUserCommands(updatedCommands);
        return { commands: updatedCommands };
      });
    },

    deleteCommand: (id) => {
      set((state) => {
        // Keep system commands even if their id is passed.
        const updatedCommands = state.commands.filter(cmd => cmd.id !== id || cmd.system);
        persistUserCommands(updatedCommands);
        return { commands: updatedCommands };
      });
    },

    resetToDefaults: () => {
      set((state) => {
        const systemCmds = state.commands.filter((c) => c.system);
        // T-198: reset user commands to empty list; system commands are
        // preserved and re-fetched from backend via loadServerDefaults.
        persistUserCommands([]);
        return { commands: [...systemCmds] };
      });
      // T-198: reload system commands from backend.
      get().loadServerDefaults();
    }
  };
});
